import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './OTPInput.css';

interface OTPInputProps {
    length: number;
    onComplete: (otp: string) => void;
    onResend?: () => void;
    expiresAt: number;
}

const OTPInput: React.FC<OTPInputProps> = ({ length, onComplete, onResend, expiresAt }) => {
    const [otp, setOtp] = useState<string[]>(new Array(length).fill(''));
    // Initialize timeLeft with actual remaining time to avoid brief "expired" flash
    const [timeLeft, setTimeLeft] = useState<number>(() =>
        Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
    );
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const { t } = useTranslation();

    useEffect(() => {
        // Focus first input
        inputRefs.current[0]?.focus();

        // Start countdown
        const interval = setInterval(() => {
            const remaining = Math.max(0, expiresAt - Date.now());
            setTimeLeft(Math.floor(remaining / 1000));

            if (remaining === 0) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [expiresAt]);

    const handleChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return; // Only allow digits

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1); // Take only last digit
        setOtp(newOtp);

        // Auto-advance to next input
        if (value && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }

        // Check if complete
        const otpString = newOtp.join('');
        if (otpString.length === length) {
            onComplete(otpString);
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain').slice(0, length);

        if (!/^\d+$/.test(pastedData)) return;

        const newOtp = pastedData.split('').concat(new Array(length - pastedData.length).fill(''));
        setOtp(newOtp);

        if (pastedData.length === length) {
            onComplete(pastedData);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="otp-container">
            <div className="otp-inputs">
                {otp.map((digit, index) => (
                    <input
                        key={index}
                        // Block body on purpose: React 19 reads a ref callback's
                        // return value as a cleanup function, so the old
                        // implicit-return arrow is now a type error.
                        ref={(el) => {
                            inputRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        className="otp-input"
                    />
                ))}
            </div>

            <div className="otp-timer">
                {timeLeft > 0 ? (
                    <span>{t('otp.validFor', { time: formatTime(timeLeft) })}</span>
                ) : (
                    <span className="expired">{t('otp.expired')}</span>
                )}
            </div>

            {timeLeft <= 270 && onResend && ( // Show after 30 seconds
                <button className="resend-button" onClick={onResend} type="button">
                    {t('otp.resend')}
                </button>
            )}
        </div>
    );
};

export default OTPInput;
